// COM-647 Task 3: frontend-side mirror of backend/src/lib/sourceListQuery.ts's
// `normalizeSourceListQuery`, the same relationship `feedRequest.ts` already
// has with its own backend counterpart (see that file's own doc comment for
// the precedent). `mockSocket.ts`'s `getManagedSources` case runs the raw
// client payload through this before it ever reaches `managedSourcesMock.ts`'s
// `buildManagedSourcesPage`, so an out-of-range `limit`/`offset` or a garbage
// `monitored`/`sortBy` value behaves the same way in e2e as it would against
// the real backend.
//
// `clamp`/`normalizeDate` are declared locally below, not imported from
// `./feedRequest` (see `normalizeDate`'s own comment further down) - fix
// round (finding 4): this header previously claimed the opposite, which was
// never true and contradicted that correct comment sitting a few lines below
// it in the same file. Kept deliberately independent of the backend module
// (different package, no shared build step) - if the backend's
// normalization rules change, this file needs a matching edit.

export type SourceSortBy = 'name' | 'createdAt';
export type SourceSortDir = 'asc' | 'desc';

export interface NormalizedSourceListQuery {
  search: string | null;
  // `null` = no filter, both monitored and unmonitored sources - the
  // default. `true`/`false` filter to exactly one. Mirrors the backend's
  // tri-state exactly (see `normalizeMonitored` below) - even the strings
  // "true"/"false" must not be coerced.
  monitored: boolean | null;
  dateFrom: string | null;
  dateTo: string | null;
  sortBy: SourceSortBy;
  sortDir: SourceSortDir;
  offset: number;
  limit: number;
}

const DEFAULT_LIMIT = 25;
const MIN_LIMIT = 1;
// Mirrors the backend's `MAX_LIMIT` - COM-639's `MAX_SOURCES_PER_PROJECT`,
// since a page of this table can never legitimately need more rows than a
// project's entire source cap.
const MAX_LIMIT = 100;
const DEFAULT_OFFSET = 0;
const DEFAULT_SORT_BY: SourceSortBy = 'createdAt';
const DEFAULT_SORT_DIR: SourceSortDir = 'desc';
const SEARCH_MAX_LENGTH = 200;

const SORT_BY_VALUES: SourceSortBy[] = ['name', 'createdAt'];
const SORT_DIR_VALUES: SourceSortDir[] = ['asc', 'desc'];

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  const floored = Math.floor(value);
  return Math.min(max, Math.max(min, floored));
}

function normalizeSortBy(value: unknown): SourceSortBy {
  return SORT_BY_VALUES.includes(value as SourceSortBy) ? (value as SourceSortBy) : DEFAULT_SORT_BY;
}

function normalizeSortDir(value: unknown): SourceSortDir {
  return SORT_DIR_VALUES.includes(value as SourceSortDir) ? (value as SourceSortDir) : DEFAULT_SORT_DIR;
}

function normalizeSearch(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  return trimmed.slice(0, SEARCH_MAX_LENGTH);
}

// Mirrors the backend's `normalizeMonitored` exactly: only a real boolean
// survives, every other shape (including the strings "true"/"false") falls
// back to null so a malformed payload degrades to "show everything" rather
// than silently filtering to one state.
function normalizeMonitored(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

// Mirrors backend `normalizeDate` exactly (also duplicated locally in
// frontend/src/lib/feedRequest.ts, not exported from there) - exact
// `YYYY-MM-DD` shape plus a real-calendar-date check via a `Date.UTC`
// round-trip, so `2026-02-31` normalizes to null instead of silently
// rolling over to March 3.
const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/;

function normalizeDate(value: unknown): string | null {
  if (typeof value !== 'string' || !DATE_FORMAT.test(value)) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  const isRealDate =
    parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;

  return isRealDate ? value : null;
}

export function normalizeSourceListQuery(raw: unknown): NormalizedSourceListQuery {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  let dateFrom = normalizeDate(source.dateFrom);
  let dateTo = normalizeDate(source.dateTo);

  // Both bounds valid but reversed: swap rather than reject, mirroring the
  // backend's own rule.
  if (dateFrom !== null && dateTo !== null && dateFrom > dateTo) {
    [dateFrom, dateTo] = [dateTo, dateFrom];
  }

  return {
    search: normalizeSearch(source.search),
    monitored: normalizeMonitored(source.monitored),
    dateFrom,
    dateTo,
    sortBy: normalizeSortBy(source.sortBy),
    sortDir: normalizeSortDir(source.sortDir),
    offset: clamp(source.offset, 0, Number.MAX_SAFE_INTEGER, DEFAULT_OFFSET),
    limit: clamp(source.limit, MIN_LIMIT, MAX_LIMIT, DEFAULT_LIMIT),
  };
}
